import {
  Button,
  SearchAutocomplete,
  Stethoscope,
  User,
  UserPlus,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { doctorApi } from "../../../services/doctor.api";
import { searchApi } from "../../../services/search.api";
import secretaryApi from "../../../services/secretary.api";
import { Doctor } from "../../../types/doctor.definition";
import { Secretary } from "../../../types/secretary.definition";
import { SearchRequest } from "../../../types/search.definition";

interface StaffSearchStepProps {
  facilityId: number;
  onAssignSuccess?: () => void;
  onCreateNew: () => void;
  isDoctorLimitReached?: boolean;
  isSecretaryLimitReached?: boolean;
  currentDoctorCount?: number;
  maxDoctors?: number;
  currentSecretaryCount?: number;
  maxSecretaries?: number;
  onUpgradeLicence?: () => void;
}

export const StaffSearchStep: React.FC<StaffSearchStepProps> = ({
  facilityId,
  onAssignSuccess,
  onCreateNew,
  isDoctorLimitReached = false,
  isSecretaryLimitReached = false,
  currentDoctorCount = 0,
  maxDoctors = 0,
  currentSecretaryCount = 0,
  maxSecretaries = 0,
  onUpgradeLicence,
}) => {
  const { t } = useTranslation();

  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [selectedSecretary, setSelectedSecretary] = useState<Secretary | null>(null);
  const [selectedSecretaryId, setSelectedSecretaryId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const searchDoctors = useCallback(async (keyword: string): Promise<Doctor[]> => {
    const payload: SearchRequest = {
      paginationSettings: { limit: 10, offset: 0 },
      sortBy: { property: "user.firstName", order: "ASC" },
      query: { keyword },
    };
    return searchApi.searchDoctorsGlobal(payload);
  }, []);

  const searchSecretaries = useCallback(async (keyword: string): Promise<Secretary[]> => {
    const payload: SearchRequest = {
      paginationSettings: { limit: 10, offset: 0 },
      sortBy: { property: "user.firstName", order: "ASC" },
      query: { keyword },
    };
    return searchApi.searchSecretariesGlobal(payload);
  }, []);

  const getDoctorDisplayValue = useCallback(
    (doctor: Doctor) => `Dr. ${doctor.firstName} ${doctor.lastName}`,
    []
  );

  const getSecretaryDisplayValue = useCallback(
    (secretary: Secretary) => `${secretary.firstName} ${secretary.lastName}`,
    []
  );

  const getDoctorSecondaryText = useCallback((doctor: Doctor) => {
    const email = doctor.contactInfo?.email || doctor.email;
    const specialties = doctor.specialties?.map((s) => s.label).join(", ");
    return [email, specialties].filter(Boolean).join(" • ");
  }, []);

  const getSecretarySecondaryText = useCallback((secretary: Secretary) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const s = secretary as any;
    const email = s.contactInfo?.email || s.email;
    const phone = s.contactInfo?.phoneNumber || s.phoneNumber;
    return [email, phone && formatPhoneNumberForDisplay(phone)]
      .filter(Boolean)
      .join(" • ");
  }, []);

  const handleDoctorSelect = useCallback((_id: string, doctor?: Doctor) => {
    setSelectedDoctor(doctor ?? null);
    setSelectedDoctorId(_id);
    setAssignError(null);
  }, []);

  const handleSecretarySelect = useCallback((_id: string, secretary?: Secretary) => {
    setSelectedSecretary(secretary ?? null);
    setSelectedSecretaryId(_id);
    setAssignError(null);
  }, []);

  const isDoctorAlreadyInFacility =
    selectedDoctor?.healthcareFacilities?.some(
      (f) => Number(f.id) === facilityId
    ) ?? false;

  const isSecretaryAlreadyInFacility =
    !!selectedSecretary && Number(selectedSecretary.healthcareFacility?.id) === facilityId;

  const handleAssignDoctor = async () => {
    if (!selectedDoctor) return;
    setIsAssigning(true);
    setAssignError(null);
    try {
      await doctorApi.assignToFacility(facilityId, selectedDoctor.id);
      onAssignSuccess?.();
    } catch {
      setAssignError(t("staff.manage.search.assignError"));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAssignSecretary = async () => {
    if (!selectedSecretary) return;
    setIsAssigning(true);
    setAssignError(null);
    try {
      await secretaryApi.assignToFacility(facilityId, selectedSecretary.id);
      onAssignSuccess?.();
    } catch {
      setAssignError(t("staff.manage.search.assignError"));
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Doctor search */}
      <div>
        <label className="block text-sm font-semibold text-neutral-800 mb-2">
          {t("staff.manage.search.doctorSection")}
        </label>
        {isDoctorLimitReached && (
          <div className="flex items-center justify-between mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              {currentDoctorCount > maxDoctors
                ? t("staff.manage.form.doctorOverLicence", {
                    count: currentDoctorCount - maxDoctors,
                    current: currentDoctorCount,
                    max: maxDoctors,
                  })
                : t("staff.manage.form.doctorLimitReached", {
                    current: currentDoctorCount,
                    max: maxDoctors,
                  })}
            </p>
            {onUpgradeLicence && (
              <button
                type="button"
                onClick={onUpgradeLicence}
                className="ml-3 shrink-0 text-xs font-medium text-yellow-800 underline hover:text-yellow-900"
              >
                {t("staff.manage.form.upgradeLicence")}
              </button>
            )}
          </div>
        )}
        <SearchAutocomplete<Doctor>
          onSelect={handleDoctorSelect}
          selectedId={selectedDoctorId}
          searchFunction={searchDoctors}
          getDisplayValue={getDoctorDisplayValue}
          getSecondaryText={getDoctorSecondaryText}
          placeholder={t("staff.manage.search.placeholder")}
          icon={Stethoscope}
          minSearchLength={2}
          getMinCharactersHint={(min) => t("common.searchMinCharacters", { count: min })}
          debounceTime={300}
        />
      </div>

      {selectedDoctor && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <Stethoscope className="h-5 w-5 text-primary-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900">
                  Dr. {selectedDoctor.firstName} {selectedDoctor.lastName}
                </p>
                <p className="text-xs text-neutral-500 truncate">
                  {[
                    selectedDoctor.contactInfo?.email || selectedDoctor.email,
                    (selectedDoctor.contactInfo?.phoneNumber || selectedDoctor.phoneNumber) &&
                    formatPhoneNumberForDisplay(
                      selectedDoctor.contactInfo?.phoneNumber || selectedDoctor.phoneNumber
                    ),
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </p>
                {(selectedDoctor.specialties?.length ?? 0) > 0 && (
                  <p className="text-xs text-neutral-400 mt-0.5 truncate">
                    {selectedDoctor.specialties!.map((s) => s.label).join(", ")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              {isDoctorAlreadyInFacility ? (
                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  {t("staff.manage.search.alreadyInFacility")}
                </span>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={handleAssignDoctor}
                    disabled={isAssigning || isDoctorLimitReached}
                  >
                    {isAssigning
                      ? t("staff.manage.search.assigning")
                      : t("staff.manage.search.assign")}
                  </Button>
                  {isDoctorLimitReached && (
                    <p className="mt-1 max-w-[12rem] text-xs text-right text-yellow-800">
                      {t("staff.manage.search.doctorQuotaReason", {
                        current: currentDoctorCount,
                        max: maxDoctors,
                      })}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 border-t border-neutral-200" />
        <span className="text-xs text-neutral-400 uppercase tracking-wide">
          {t("staff.manage.search.or")}
        </span>
        <div className="flex-1 border-t border-neutral-200" />
      </div>

      {/* Secretary search */}
      <div>
        <label className="block text-sm font-semibold text-neutral-800 mb-2">
          {t("staff.manage.search.secretarySection")}
        </label>
        {isSecretaryLimitReached && (
          <div className="flex items-center justify-between mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              {currentSecretaryCount > maxSecretaries
                ? t("staff.manage.form.secretaryOverLicence", {
                    count: currentSecretaryCount - maxSecretaries,
                    current: currentSecretaryCount,
                    max: maxSecretaries,
                  })
                : t("staff.manage.form.secretaryLimitReached", {
                    current: currentSecretaryCount,
                    max: maxSecretaries,
                  })}
            </p>
            {onUpgradeLicence && (
              <button
                type="button"
                onClick={onUpgradeLicence}
                className="ml-3 shrink-0 text-xs font-medium text-yellow-800 underline hover:text-yellow-900"
              >
                {t("staff.manage.form.upgradeLicence")}
              </button>
            )}
          </div>
        )}
        <SearchAutocomplete<Secretary>
          onSelect={handleSecretarySelect}
          selectedId={selectedSecretaryId}
          searchFunction={searchSecretaries}
          getDisplayValue={getSecretaryDisplayValue}
          getSecondaryText={getSecretarySecondaryText}
          placeholder={t("staff.manage.search.placeholder")}
          icon={User}
          minSearchLength={2}
          getMinCharactersHint={(min) => t("common.searchMinCharacters", { count: min })}
          debounceTime={300}
        />
      </div>

      {selectedSecretary && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-secondary-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900">
                  {selectedSecretary.firstName} {selectedSecretary.lastName}
                </p>
                <p className="text-xs text-neutral-500 truncate">
                  {getSecretarySecondaryText(selectedSecretary)}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              {isSecretaryAlreadyInFacility ? (
                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  {t("staff.manage.search.alreadyInFacility")}
                </span>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={handleAssignSecretary}
                    disabled={isAssigning || isSecretaryLimitReached}
                  >
                    {isAssigning
                      ? t("staff.manage.search.assigning")
                      : t("staff.manage.search.assign")}
                  </Button>
                  {isSecretaryLimitReached && (
                    <p className="mt-1 max-w-[12rem] text-xs text-right text-yellow-800">
                      {t("staff.manage.search.secretaryQuotaReason", {
                        current: currentSecretaryCount,
                        max: maxSecretaries,
                      })}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {assignError && (
        <p className="text-xs text-red-600">{assignError}</p>
      )}

      {/* Create new */}
      <div className="pt-4 border-t border-neutral-200">
        <button
          type="button"
          onClick={onCreateNew}
          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          {t("staff.manage.search.createNew")}
        </button>
      </div>
    </div>
  );
};
